import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryResult,
  useQueryClient,
  UseQueryOptions,
  keepPreviousData,
} from "@tanstack/react-query";

import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { TResponse, TResponseWithQData } from "../../../types/common";
import { QData } from "../../../types/basic-type-list";
import { useState } from "react";
import { IndustryDetail, IndustryItem } from "@src/types/api/industry";
import {
  deleteIndustry,
  industryDetail,
  listIndustry,
  saveIndustry,
} from "./callback";

export const useIndustrySave = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IndustryDetail>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<
    TResponse<IndustryDetail>,
    unknown,
    IndustryDetail
  > = {}
): UseMutationResult<TResponse<IndustryDetail>, unknown, IndustryDetail> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IndustryDetail>, unknown, IndustryDetail>({
    mutationFn: async (data) => {
      const res = await saveIndustry(data);
      return res.data;
    },

    onSuccess: (res, variables, context, mutation) => {
      queryClient.setQueryData(
        [API_ENDPOINTS.INDUSTRY_LIST, { ...qData, total: null }],
        (old?: TResponseWithQData<IndustryDetail[]>) => {
          if (!old || !res.data) return old;

          const exists = old.data.find((x) => x._id === res.data._id);

          return {
            ...old,
            data: exists
              ? old.data.map((x) =>
                  x._id === res.data!._id ? res.data! : x
                )
              : [res.data!, ...old.data],
          };
        }
      );

      onSuccess?.(res);
      options.onSuccess?.(res, variables, context, mutation);
    },

    onError: (err, variables, context, mutation) => {
      onError?.(err);
      options.onError?.(err, variables, context, mutation);
    },

    ...options,
  });
};


export const useIndustryDetail = (
  slug: string
): UseQueryResult<TResponse<IndustryDetail>, Error> => {
  return useQuery({
    queryKey: [API_ENDPOINTS.INDUSTRY_DETAIL, slug],
    queryFn: async () => {
      const res = await industryDetail(slug);
      return res.data;
    },
    enabled: !!slug,
    refetchOnWindowFocus: false,
    staleTime: 300000,
  });
};


export const useIndustryList = (
  qData: QData,
  options?: Omit<
    UseQueryOptions<TResponseWithQData<IndustryItem[]>, Error>,
    "queryKey" | "queryFn"
  >
): UseQueryResult<TResponseWithQData<IndustryItem[]>, Error> & {
  refetchWithQuery: (params: Partial<QData>) => void;
} => {
  const [queryParams, setQueryParams] = useState<QData>({ ...qData });

  const queryResult = useQuery<TResponseWithQData<IndustryItem[]>, Error>({
    queryKey: [API_ENDPOINTS.INDUSTRY_LIST, { ...queryParams, total: null }],
    queryFn: async () => {
      const res = await listIndustry(queryParams);
      return res.data;
    },
    select: (res) => ({
      ...res,
      qData: {
        ...queryParams,
        total: res.extra?.total,
      },
    }),
    placeholderData: keepPreviousData,
    staleTime: 300000,
    refetchOnWindowFocus: false,
    ...options,
  });

  return {
    ...queryResult,
    refetchWithQuery: (params) => {
      setQueryParams((prev) => ({ ...prev, ...params }));
    },
  };
};


export const useIndustryDelete = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: () => void;
    onError?: (error: unknown) => void;
  } = {},
  options?: UseMutationOptions<
    TResponse<null>,
    unknown,
    { id: string | string[] }
  >
): UseMutationResult<
  TResponse<null>,
  unknown,
  { id: string | string[] }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      const res = await deleteIndustry(payload);
      return res.data;
    },

    onSuccess: (res, payload, context, mutation) => {
      queryClient.setQueryData(
        [API_ENDPOINTS.INDUSTRY_LIST, { ...qData, total: null }],
        (old?: TResponseWithQData<IndustryDetail[]>) => {
          if (!old) return old;

          return {
            ...old,
            data: Array.isArray(payload.id)
              ? old.data.filter((x) => x._id && !payload.id.includes(x._id))
              : old.data.filter((x) => x._id !== payload.id),
          };
        }
      );

      onSuccess?.();
      options?.onSuccess?.(res, payload, context, mutation);
    },

    onError: (err, payload, context, mutation) => {
      onError?.(err);
      options?.onError?.(err, payload, context, mutation); 
    },

    ...options,
  });
};